<template>
	<DsBlock v-if="loaded" grid :background="background" gradient-size="md" class="c-home-hero">
		<div class="c-home-hero__header">
			<DsTag v-if="tag" class="c-home-hero__tag" :text="tag"></DsTag>

			<DsText
				v-if="title"
				class="c-home-hero__title"
				html-element="h1"
				text-type="heading-8xl"
				text-style="medium"
			>
				{{ title }}
			</DsText>

			<DsText
				v-if="text"
				class="c-home-hero__text"
				html-element="p"
				text-type="heading-2xl"
				text-style="regular"
			>
				{{ text }}
			</DsText>
		</div>

		<div class="c-home-hero__images" ref="heroViewport">
			<div class="c-home-hero__grid-image-big">
				<ImageFader
					:slides="largeSlides"
					:current-index="currentItemIndex"
					:delayMs="100"
					width="560"
					height="721"
					containerClass="c-home-hero__image-big-container"
					imgClass="c-home-hero__image-big"
					mode="fade"
					:transitionMs="700"
					preload="all"
				/>
			</div>

			<div class="c-home-hero__grid-image-small">
				<Transition name="caption-transition" mode="out-in">
					<div
						v-if="hasCaptionContent"
						:key="captionKey"
						class="c-home-hero__caption"
					>
						<DsText
							v-if="currentItem.username"
							class="c-home-hero__caption-name"
							text-type="body-sm"
							text-style="medium"
						>
							{{ currentItem.username }}
							<DsUnderscore></DsUnderscore>
						</DsText>

						<DsText
							v-if="currentItem.userRole || currentItem.userText"
							class="c-home-hero__caption-text"
							text-type="body-sm"
							text-style="regular"
						>
							{{ captionText }}
						</DsText>
					</div>
				</Transition>

				<ImageFader
					:slides="smallSlides"
					:current-index="currentItemIndex"
					:delayMs="200"
					width="344"
					height="397"
					containerClass="c-home-hero__image-small-container"
					imgClass="c-home-hero__image-small"
					mode="fade"
					:transitionMs="600"
					preload="all"
				/>

				<div v-if="shouldShowControls" class="c-home-hero__controls">
					<PlayPauseIndicator
						:aria-label-pause="ariaLabelPauseText"
						:aria-label-play="ariaLabelPlayText"
						:is-playing="isPlaying"
						:pause="pauseAutoplay"
						:play="playAutoplay"
						:progress="progress"
						:current-item="currentItemIndex + 1"
						:total-items="numItems"
					></PlayPauseIndicator>
				</div>
			</div>
		</div>
	</DsBlock>

	<slot v-else></slot>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { DsBlock, DsTag, DsText, DsUnderscore } from '@coloplast/design-system';

import ImageFader from './components/ImageFader.vue';
import PlayPauseIndicator from './components/PlayPauseIndicator.vue';
import { useSlideTimer } from './composables/useSlideTimer';
import { useViewportAutoplay } from './composables/useViewportAutoplay';

export type ConsumerHomepageHeroItem = {
	background: 'gradient-1' | 'gradient-2' | 'gradient-3' | 'gradient-4';
	largeImageUrl: string;
	largeImageAltText?: string;
	smallImageUrl: string;
	smallImageAltText?: string;
	username?: string;
	userRole?: string;
	userText?: string;
};

export type ConsumerHomepageHeroProps = {
	ariaLabelPause?: string;
	ariaLabelPlay?: string;
	tag?: string;
	title: string;
	text: string;
	items: {
		baseViewModel: Array<ConsumerHomepageHeroItem>;
	};
	durationSeconds?: number;
};

const props = withDefaults(defineProps<ConsumerHomepageHeroProps>(), {
	durationSeconds: 4,
});

const IMAGE_SRCSETS = {
	LARGE: [456, 560, 912, 1120],
	SMALL: [280, 350, 560, 700],
} as const;

const IMAGE_SIZES = {
	LARGE: '(min-width: 992px) min(456px, 33vw), min(560px, 56.5vw)',
	SMALL: '(min-width: 992px) min(280px, 20vw), min(350px, 35.3vw)',
} as const;

const loaded = ref(false);
const currentItemIndex = ref(0);
const heroViewport = useTemplateRef<HTMLElement>('heroViewport');

const items = computed(() => props.items.baseViewModel);
const numItems = computed(() => items.value.length);
const shouldAutoplay = computed(() => numItems.value > 1);
const shouldShowControls = computed(() => shouldAutoplay.value);
const duration = computed(() => Math.max(2, props.durationSeconds ?? 4));

const currentItem = computed(() => {
	return items.value[currentItemIndex.value];
});

const background = computed(() => {
	return currentItem.value.background;
});

const hasCaptionContent = computed(() => {
	return Boolean(
		currentItem.value.username ||
		currentItem.value.userRole ||
		currentItem.value.userText
	);
});

const captionKey = computed(() => {
	return `${currentItem.value.username}${currentItem.value.userRole}${currentItem.value.userText}`;
});

const captionText = computed(() => {
	const role = currentItem.value.userRole ? `${currentItem.value.userRole} | ` : '';

	return `${role}${currentItem.value.userText || ''}`;
});

const ariaLabelPauseText = computed(() => {
	return props.ariaLabelPause?.trim() || 'Pause';
});

const ariaLabelPlayText = computed(() => {
	return props.ariaLabelPlay?.trim() || 'Play';
});

const largeSlides = computed(() => {
	return items.value.map((item) => ({
		src: withWidth(item.largeImageUrl, IMAGE_SRCSETS.LARGE[2]),
		srcset: buildSrcset(item.largeImageUrl, IMAGE_SRCSETS.LARGE),
		sizes: IMAGE_SIZES.LARGE,
		alt: item.largeImageAltText || '',
	}));
});

const smallSlides = computed(() => {
	return items.value.map((item) => ({
		src: withWidth(item.smallImageUrl, IMAGE_SRCSETS.SMALL[2]),
		srcset: buildSrcset(item.smallImageUrl, IMAGE_SRCSETS.SMALL),
		sizes: IMAGE_SIZES.SMALL,
		alt: item.smallImageAltText || '',
	}));
});

const tick = () => {
	if (!shouldAutoplay.value) {
		return;
	}

	currentItemIndex.value = (currentItemIndex.value + 1) % numItems.value;
	progress.value = 0;
};

const { isPlaying, progress, play, pause } = useSlideTimer({
	durationSeconds: duration,
	autoStart: shouldAutoplay.value,
	respectReducedMotion: true,
	onTick: tick,
});

const playAutoplay = () => {
	if (!shouldAutoplay.value) {
		return;
	}

	play();
};

const pauseAutoplay = () => {
	pause();
};

const withWidth = (url: string, width: number) => {
	const [path, queryString = ''] = url.split('?');
	const params = new URLSearchParams(queryString);

	params.set('width', String(width));

	const query = params.toString();

	return query ? `${path}?${query}` : path;
};

const buildSrcset = (url: string, widths: readonly number[]) => {
	return widths.map((width) => `${withWidth(url, width)} ${width}w`).join(', ');
};

useViewportAutoplay(
	heroViewport,
	{ isPlaying, play: playAutoplay, pause: pauseAutoplay },
	{ pauseThreshold: 0.35, resumeThreshold: 0.45 }
);

onMounted(() => {
	loaded.value = true;
});
</script>

<style lang="scss">
.c-home-hero {
	.ds-button-play-progress {
		--button-diameter: 44px;
	}
}

.caption-transition-enter-active,
.caption-transition-leave-active {
	transition: opacity 0.3s ease;
}

.caption-transition-enter-from,
.caption-transition-leave-to {
	opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
	.caption-transition-enter-active,
	.caption-transition-leave-active {
		transition: none;
	}
}
</style>
